import React, { forwardRef } from 'react'
import type { StyleProp } from '@vtex/admin-ui'
import { Text, csx } from '@vtex/admin-ui'
import { useFragment } from 'react-relay-offline'
import { graphql } from 'babel-plugin-relay/macro'

import { Avatar } from '../Avatar'
import { List } from '../../shared/types'
import type { OutOfStoreListItem_salesAssociate$key } from './__generated__/OutOfStoreListItem_salesAssociate.graphql'
import type { ListsItem } from '../../context/TurnListProvider'
import type { OutOfStoreListItem_moviment$key } from './__generated__/OutOfStoreListItem_moviment.graphql'
import DraggableCard from '../ListItem/DraggableCard'

const textStyles: StyleProp = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '6.5rem',
  textAlign: 'center',
}

type Props = {
  outOfStoreItem: ListsItem['outOfStore'] & {
    salesAssociate: OutOfStoreListItem_salesAssociate$key
  }
  index: number
  usedAsDragPreview?: boolean
  'data-testid'?: string
}

export const OutOfStoreListItem = forwardRef(function OutOfStoreListItem(
  {
    outOfStoreItem,
    index,
    usedAsDragPreview,
    'data-testid': dataTestId,
  }: Props,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const salesAssociate = useFragment<OutOfStoreListItem_salesAssociate$key>(
    graphql`
      fragment OutOfStoreListItem_salesAssociate on SalesAssociate {
        name
        avatar {
          url
        }
      }
    `,
    outOfStoreItem.salesAssociate
  )

  const lastMovement = useFragment<OutOfStoreListItem_moviment$key>(
    graphql`
      fragment OutOfStoreListItem_moviment on SalesAssociateTrackingMovementStep {
        name
        icon {
          ... on AdminUiIcon {
            id
          }
        }
      }
    `,
    outOfStoreItem.lastMovement
  )

  return (
    <DraggableCard
      ref={ref}
      item={outOfStoreItem}
      dndType={List.OutOfStore}
      index={index}
      className={csx({
        display: 'flex',
        justifyContent: 'center',
        padding: '$space-3',
        flexDirection: 'column',
        alignItems: 'center',
        width: '8rem',
        height: '7.5rem',
        marginRight: '$space-4',
        ':last-child': {
          marginRight: '$space-0',
        },
        '@desktop': {
          paddingX: '$space-0',
          width: '10rem',
        },
      })}
      data-testid={dataTestId ?? 'out-of-store-list-item'}
      usedAsDragPreview={usedAsDragPreview}
    >
      <Avatar
        src={salesAssociate?.avatar?.url}
        icon={lastMovement?.icon?.id}
        size={2}
      />
      <Text
        className={csx({ ...textStyles, text: '$body', marginY: '$space-1' })}
      >
        {salesAssociate?.name}
      </Text>
      <Text
        className={csx({ ...textStyles, text: '$detail', color: '$secondary' })}
      >
        {lastMovement?.name}
      </Text>
    </DraggableCard>
  )
})
